import * as Sentry from '@sentry/browser';

const envMetaEl : HTMLMetaElement | null = document.querySelector("meta[name='environment']");
const dsnMetaEl : HTMLMetaElement | null = document.querySelector("meta[name='sentry']");
const environment = envMetaEl?.content || "unknown";
const dsn = dsnMetaEl?.content;

if (Sentry.isInitialized()) {
  console.log("Skipping Sentry configuration, already initialized");
} else if (dsn) {
  const release = import.meta.env.VITE_APP_VERSION ? `javascript@${import.meta.env.VITE_APP_VERSION}` : undefined;

  Sentry.init({
    dsn: dsn,
    integrations: [Sentry.browserTracingIntegration()],
    environment: environment,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      // https://github.com/getsentry/sentry/issues/5267
      'SecurityError: Blocked a frame with origin "https://www.delivereasy.co.nz" from accessing',
      // https://www.fillr.com SDK calls something like this.fillrFrame.startGetFields and it blows up
      // all the time. We used to grep for a fairly specific message, but the different js engines
      // produce very different error messages and this method name is unique enough that it's probably
      // safe enough to just fully ignore it.
      'startGetFields',
    ],
    tracesSampleRate: 0.01,
    release: release,
    // Force enable the ip reporting
    // The SDK usually only enables this if you have enabled the default PII collection,
    // but they explicitly don't override values set here so this should be stable-ish.
    _metadata: { sdk: { settings: { infer_ip: "auto" } } },
    beforeSend: (event) => {
      const ex = event.exception?.values?.[0];
      const frames = ex?.stacktrace?.frames ?? [];
      const innermost = frames[frames.length - 1]?.filename ?? '';

      if (ex && _isAbortedGtagFetch(ex, innermost)) {
        event.fingerprint = ['gtag-aborted-fetch'];
      }

      return event;
    },
  });

  if (window.sentryExtraUserInfo != undefined) {
    Sentry.setUser(window.sentryExtraUserInfo);
  }
} else {
  console.warn("Skipping Sentry configuration because no DSN was supplied");
}

const _isAbortedGtagFetch = (exception: Sentry.Exception, innermost_frame_filename: string) => {
  return exception.type === 'TypeError'
    && /^(Load failed|Failed to fetch|NetworkError when attempting to fetch resource)/.test(exception.value ?? '')
    && exception.mechanism?.type === 'auto.browser.global_handlers.onunhandledrejection'
    && /\/gtag\/(js|destination)/.test(innermost_frame_filename);
}
